import { useTaskProcessor } from '@eluve/appointment-tasks';
import { Button, Stack } from '@eluve/components';
import {
  useAppointmentActivity,
  useAppointmentStatus,
  useIsAppointmentReadonly,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { AppointmentStatusTypesLookup } from '@eluve/graphql-types';
import { useCaptureEvent } from '@eluve/posthog-react';
import {
  FeatureFlaggedComponent,
  useSelectedMicrophoneDevice,
} from '@eluve/smart-blocks';

import { useAppointmentDialogs } from '../AppointmentDialogProvider';
import { useIsRecordingTooShortForNote } from '../machines/transcription/hooks/useRecordingDurationGuard';
import { useStartAppointmentRecording } from '../machines/transcription/hooks/useStartAppointmentRecording';
import { useTranscriptionMachineSelector } from '../machines/transcription/hooks/useTranscriptionMachine';

import { ShortRecordingNoteGuardTooltip } from './ShortRecordingNoteGuardTooltip';
import { UploadAudioFileButton } from './UploadAudioFileButton';

interface TranscriptionControlsProps {
  tenantId: string;
  appointmentId: string;
  onGenerateNote?: () => void;
}

export const AppointmentTranscriptionIdleControls: React.FC<
  TranscriptionControlsProps
> = ({ tenantId, appointmentId, onGenerateNote }) => {
  const isReadonly = useIsAppointmentReadonly();
  const appointmentStatus = useAppointmentStatus();
  const [selectedMicId] = useSelectedMicrophoneDevice();
  const { isSummaryAvailable } = useSummary();
  const { isSummarizationInProgress } = useAppointmentActivity();

  const { openOverwriteSummaryDialog } = useAppointmentDialogs();
  const { taskProcessor } = useTaskProcessor();

  const isGeneratingNote =
    useTranscriptionMachineSelector((s) =>
      s.matches({ InSession: 'SessionEnded' }),
    ) || isSummarizationInProgress;

  const { startAppointmentRecording, isStartingAppointmentRecording } =
    useStartAppointmentRecording({
      tenantId,
      appointmentId,
      selectedMicId,
    });

  const captureEvent = useCaptureEvent({
    appointmentId,
  });

  const handleResumeClick = () => {
    startAppointmentRecording();
  };

  const isCompleted =
    appointmentStatus === AppointmentStatusTypesLookup.COMPLETED;

  const isTooShort = useIsRecordingTooShortForNote();

  const handleGenerateNoteClick = () => {
    if (isGeneratingNote || isTooShort) {
      return;
    }

    captureEvent('ended_session');
    onGenerateNote?.();

    if (isSummaryAvailable) {
      openOverwriteSummaryDialog(appointmentId);
    } else {
      taskProcessor.addTask({
        type: 'TASK.GENERATE_NOTE',
        appointmentId,
        userCompletedAt: new Date().toISOString(),
      });
    }
  };

  if (isReadonly) {
    return null;
  }

  return (
    <Stack direction="horizontal" fullWidth={false} gap={2}>
      <FeatureFlaggedComponent flag="ALLOW_AUDIO_FILE_UPLOAD_FOR_APPOINTMENT">
        <UploadAudioFileButton appointmentId={appointmentId} buttonSize="md" />
      </FeatureFlaggedComponent>
      <Button
        variant="secondary"
        disabled={isGeneratingNote}
        loading={isStartingAppointmentRecording}
        onClick={handleResumeClick}
      >
        Resume Session
      </Button>
      <ShortRecordingNoteGuardTooltip active={isTooShort}>
        <Button
          data-accessibility-label="generate-note-button"
          disabled={isCompleted || isTooShort}
          loading={isGeneratingNote}
          onClick={handleGenerateNoteClick}
        >
          Generate Note
        </Button>
      </ShortRecordingNoteGuardTooltip>
    </Stack>
  );
};
